import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal, Spin, Icon } from 'antd';
import moment from 'moment';
import classNames from 'classnames';
import useIntersectionObserver from '../../../../hooks/useIntersectionObserver';
import useCommentAttachments from '../../../../queries/useRequestCommentAttachments';
import getCommentAttachment from '../../../../requests/getRequestCommentAttachment';
import getAttachmentType from '../../../../helpers/getAttachmentType';
import MessageOption from './MessageOption';
import Images from './Images';
import Files from './Files';
import useChangeNoteAvailability from '../../../../hooks/useChangeRequestNoteAvailability';
import styles from './Comment.module.scss';
import { linkify } from '../../../../helpers/linkify';

const { confirm } = Modal;

const Comment = ({
  containerRef,
  belongsToCurrentUser = false,
  commentData,
  onEditBtnClick,
  onDeleteComment,
  editedCommentData,
  requestId,
  canChange,
}) => {
  const commentRef = useRef();

  const [hasAttachment, setHasAttachment] = useState(false);
  const [commentImages, setCommentImages] = useState([]);
  const [commentFiles, setCommentFiles] = useState([]);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);

  const { data: attachments, isFetching: isAttachmentsFetching } =
    useCommentAttachments(commentData.id, hasAttachment);

  const getUrlFromBlob = (string) => {
    const data = Buffer.from(string, 'base64');
    return window.URL.createObjectURL(new Blob([data]));
  };

  const getCommentAttachments = useCallback(async () => {
    if (attachments && attachments.length) {
      setIsAttachmentsLoading(true);

      const attachmentsWithFile = await Promise.all(
        attachments.map(async (attachment) => {
          const file = await getCommentAttachment(
            commentData.id,
            attachment.fileName
          );

          return { ...attachment, file };
        })
      );

      let images = [];
      let files = [];

      attachmentsWithFile.forEach((attachment) => {
        const attachmentType = getAttachmentType(attachment.fileName);

        if (attachmentType === 'picture') {
          attachment.src = getUrlFromBlob(attachment.file);
          images.push(attachment);
        } else {
          files.push(attachment);
        }
      });

      setCommentImages(images);
      setCommentFiles(files);
      setIsAttachmentsLoading(false);
    }
  }, [attachments, commentData.id]);

  const { mutate: changeAvailability, isLoading: isChangeAvailabilityLoading } =
    useChangeNoteAvailability(requestId);

  const handleChangeAvailability = () => {
    changeAvailability({
      noteId: commentData.id,
      availableForClient: !commentData.availableForClient,
    });
  };

  useEffect(() => {
    getCommentAttachments();
  }, [getCommentAttachments]);

  const handleIntersect = async ([{ isIntersecting }], observerElement) => {
    if (isIntersecting) {
      if (commentData.hasAttachment) {
        setHasAttachment(true);
      } else {
        setHasAttachment(false);
      }

      observerElement.unobserve(commentRef.current);
    }
  };

  useIntersectionObserver({
    containerRef,
    target: commentRef,
    onIntersect: handleIntersect,
  });

  const handleEditBtnClick = () => {
    const data = { ...commentData, commentImages, commentFiles };
    if (editedCommentData) {
      confirm({
        title: 'Вы действительно хотите редактировать этот комментарий?',
        onOk: () => onEditBtnClick(data),
        onCancel: () => {},
      });
    } else {
      onEditBtnClick(data);
    }
  };

  const handleDeleteBtnClick = () => {
    confirm({
      title: 'Вы действительно хотите удалить этот комментарий?',
      content: 'Его нельзя будет восстановить.',
      onOk: () => onDeleteComment(commentData.id),
      onCancel: () => {},
    });
  };

  const messageStyles = useMemo(() => {
    const messageStyles = [];
    if (commentData.isSystemMessage) {
      messageStyles.push(styles.systemMessage);
    } else if (belongsToCurrentUser) {
      messageStyles.push(styles.belongsToCurrentUser);
    } else {
      messageStyles.push(styles.doesntBelongToCurrentUser);
    }

    if (editedCommentData && editedCommentData.id === commentData.id) {
      messageStyles.push(styles.currentlyEditedMessage);
    }

    return messageStyles;
  }, [commentData, editedCommentData]);

  return (
    <div
      comment-data-item="true"
      className={classNames(styles.chatComment, ...messageStyles)}
      ref={commentRef}
    >
      {!commentData.isSystemMessage && belongsToCurrentUser && (
        <MessageOption
          onEditBtnClick={
            commentData.canBeEdited && canChange ? handleEditBtnClick : undefined
          }
          onDeleteBtnClick={canChange ? handleDeleteBtnClick : undefined}
          onChangeAvailability={
            !commentData.fromClientNote && canChange ? handleChangeAvailability : undefined
          }
          availableForClient={commentData.availableForClient}
        />
      )}
      <div className={styles.chatMessageContent}>
        {isChangeAvailabilityLoading && (
          <Spin
            spinning
            className={styles.commentSpin}
          />
        )}
        {commentData.hasAttachment &&
          (isAttachmentsFetching || isAttachmentsLoading) && (
            <Spin className={styles.spinImages} spinning />
          )}
        {!!commentImages.length && <Images images={commentImages} />}
        <div
          className={styles.text}
          dangerouslySetInnerHTML={{
            __html: linkify(
              commentData?.note === '(Вложение)' ? '' : commentData?.note || ''
            ),
          }}
        />
        {!!commentFiles.length && <Files files={commentFiles} />}
        {!commentData.fromClientNote && !commentData.availableForClient && (
          <div className={styles.notAvailableForClient}>
            <Icon type="eye-invisible" style={{ fontSize: '16px' }} />
            Скрыто от собственника
          </div>
        )}
        <div className={styles.footer}>
          <div className={styles.dateAndChange}>
            {commentData?.date &&
              moment(commentData.date).format('DD.MM.YYYY HH:mm')}
            {commentData.edited && <div>изменено</div>}
          </div>
          <div className={styles.userContainer}>
            {commentData?.user?.specialityName && (
              <span className={styles.speciality}>
                {commentData.user.specialityName}
              </span>
            )}
            <span>{commentData?.user?.fullName || ''}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Comment);
